import { toSnakeCase } from '../utils/caseConverter';
const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL as string;

export const fetchBookings = async () => {
  const res = await fetch(`${API_BASE_URL}/bookings`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to fetch bookings: ${res.status} ${text}`);
  }
  const data = await res.json();
  const list: any[] = Array.isArray(data)
    ? data
    : Array.isArray((data || {}).bookings)
      ? (data as any).bookings
      : [];

  const mapName = (travellers: any): string => {
    try {
      const flat = Array.isArray(travellers) ? travellers.flat(Infinity) : [];
      const lead = flat && flat.length > 0 ? flat[0] : null;
      const fn = (lead?.firstName || lead?.first_name || '').trim();
      const ln = (lead?.lastName || lead?.last_name || '').trim();
      const name = `${fn} ${ln}`.trim();
      return name || '—';
    } catch {
      return '—';
    }
  };

  const mapped = list.map((b: any) => {
    const amountPaise = Number(b.amount_paise || 0);
    const totalPrice = isFinite(amountPaise) && amountPaise > 0
      ? Math.round(amountPaise) / 100
      : (Number(b?.pricing?.pay) || Number(b?.pricing?.total) || 0);
    const customerName = mapName(b.travellers);
    const email = b?.contact?.email || '';
    const phone = b?.contact?.phone || '';
    return {
      // legacy keys used by UI
      id: b.id,
      query_id: b.query_id ? Number(b.query_id) : undefined,
      parent_id: b.parent_id || null,
      status: b.status || '—',
      payment_status: b.payment_status || '',
      created_at: b.created_at,
      updated_at: b.updated_at,
      travel_start_date: b.start_at,
      travel_end_date: b.end_at,
      total_price: totalPrice,
      currency: b.currency || 'INR',
      product_type: b.product_type || '',
      provider_ref: b.provider_ref,
      // enrich for UI search and display
      package: { title: b.title || (b.product?.hotel?.name) || 'Booking' },
      customer: { id: b.customer_id ? Number(b.customer_id) : undefined, name: customerName, email, phoneNumber: phone },
      payments: [],
    } as any;
  });
  return mapped;
};

export async function assignBooking(bookingId: number | string, userId: number): Promise<{ id: number | string }> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify({ "assigned_to": userId })
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to assign booking: ${errorText}`);
  }

  return res.json();
}

export async function addPayment(bookingId: number | string, paymentData: { amount: number; method: string; transactionId: string; notes?: string; }): Promise<{ id: number | string }> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify(paymentData)
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to add payment: ${errorText}`);
  }

  return res.json();
}

export async function paymentReminder(bookingId: number | string): Promise<{ id: number | string }> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}/payments/reminder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to send payment reminder: ${errorText}`);
  }

  return res.json();
}

export interface CreateBookingPayload {
  queryId: number;
  travelStartDate?: string;
  travelEndDate?: string;
  totalPrice?: number;
  status?: string;
  notes?: string;
  providerRef?: any;
}

export async function createBooking(payload: CreateBookingPayload) {
  const res = await fetch(`${API_BASE_URL}/bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify(toSnakeCase(payload)),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to create booking: ${errorText}`);
  }
  return res.json();
}

export async function updateBooking(bookingId: number | string, payload: Partial<CreateBookingPayload>) {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify(toSnakeCase(payload)),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to update booking: ${errorText}`);
  }
  return res.json();
}
